import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router";

import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { ScormExportDialog } from "sj-ui-toolkit/src/atlas/components";
import type { ScormLesson } from "sj-ui-toolkit/src/helpers/scorm/generateScormPackage";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { formatMinutes } from "sj-ui-toolkit/src/utils/time";
import { getLessonBadgeSvg } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardHelpers";
import type { ContentType } from "sj-ui-toolkit/src/atlas/components/ContentCard/contentCardTypes";

import { PathsContext } from "@/context/PathsContext";
import {
  toggleActivated,
  toggleRestrictAsync,
  deletePath,
  getActivityPathConflict,
} from "@/api/paths";
import type { PathData } from "@/api/paths";
import PathDetailsSkeleton from "./PathDetailsSkeleton";
import ClonePathModal from "./ClonePathModal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VersionOption {
  label: string;
  value: number;
}

interface Lesson {
  id: string | number;
  name?: string;
  [key: string]: unknown;
}

interface PathDetailsAdminProps {
  pathDetails: Partial<PathData> | null;
  pathType: string;
  onActivationUpdate: (activated: boolean) => void;
  onRestrictionUpdate?: (restricted: boolean) => void;
  loading?: boolean;
  allPathNames?: string[];
  lessons?: Lesson[];
  versionOptions?: VersionOption[];
  selectedVersionIndex?: number;
  onVersionChange?: (index: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PathDetailsAdmin = ({
  pathDetails,
  pathType,
  onActivationUpdate,
  onRestrictionUpdate,
  loading = false,
  allPathNames = [],
  lessons = [],
  versionOptions = [],
  selectedVersionIndex = 0,
  onVersionChange,
}: PathDetailsAdminProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { allPaths, updatePathActivation } = useContext(PathsContext);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [localPathDetails, setLocalPathDetails] = useState<Partial<PathData> | null>(pathDetails);
  const [isScormExportVisible, setIsScormExportVisible] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isCloneModalVisible, setIsCloneModalVisible] = useState(false);

  useEffect(() => {
    setLocalPathDetails(pathDetails);
  }, [pathDetails]);

  const badgeSvg = getLessonBadgeSvg(
    (localPathDetails as { type?: ContentType } | null)?.type,
    "notStarted"
  );
  const isCustomOrQuest = !(localPathDetails as { isSjContent?: boolean } | null)?.isSjContent;
  const isLatestVersion = versionOptions.length <= 1 || selectedVersionIndex === 0;

  const handleToggleActivated = async () => {
    const newActivatedState = !localPathDetails?.activated;

    if (newActivatedState) {
      const conflict = getActivityPathConflict(localPathDetails?.rankName, allPaths);
      if (conflict) {
        showToast({
          severity: "warn",
          summary: "Active path conflict",
          detail: `"${conflict.name}" is the currently active ${localPathDetails?.levelName ?? localPathDetails?.rankName} path. Deactivate it before activating "${localPathDetails?.name}".`,
          life: 8000,
        });
        return;
      }
    }

    setActionLoading("activate");
    try {
      await toggleActivated({
        id: localPathDetails?.id ?? "",
        paths: [localPathDetails as PathData],
        type: (localPathDetails as { type?: string } | null)?.type ?? null,
        setData: () => {},
        showToast,
        onSuccess: () => {
          onActivationUpdate(newActivatedState);
          setLocalPathDetails(prev => (prev ? { ...prev, activated: newActivatedState } : prev));
          if (localPathDetails?.id != null) {
            updatePathActivation(localPathDetails.id, newActivatedState);
          }
        },
        onRevert: () => {
          if (localPathDetails?.id != null) {
            updatePathActivation(localPathDetails.id, !newActivatedState);
          }
        },
      });
    } catch {
      // Error handled in helper
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRestrict = async () => {
    setActionLoading("restrict");
    const newRestricted = !localPathDetails?.restricted;
    setLocalPathDetails(prev => (prev ? { ...prev, restricted: newRestricted } : prev));
    try {
      await toggleRestrictAsync(localPathDetails as PathData, showToast); // options defaults to {} (toggle)
      onRestrictionUpdate?.(newRestricted);
    } catch {
      setLocalPathDetails(prev => (prev ? { ...prev, restricted: !newRestricted } : prev));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setActionLoading("delete");
    setShowDeleteConfirm(false);
    try {
      await deletePath(localPathDetails as PathData, navigate, showToast);
      navigate(AdminAppRoutes.routes.paths.routes.custom.route);
    } catch {
      // Error handled in helper
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <PathDetailsSkeleton />;

  return (
    <div className="pb-8">
      {/* Status tags + version selector + action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-8 mb-8">
        <div className="flex items-center gap-4">
          <Tag value={pathType.toUpperCase()} severity="contrast" rounded />
          {localPathDetails?.activated ? (
            <Tag value="ACTIVE" severity="success" rounded />
          ) : (
            <Tag value="INACTIVE" severity="warning" rounded />
          )}
          {localPathDetails?.restricted && <Tag value="RESTRICTED" severity="info" rounded />}
          {versionOptions.length > 1 && (
            <Dropdown
              value={selectedVersionIndex}
              onChange={e => onVersionChange?.(e.value as number)}
              options={versionOptions}
              placeholder="Select Version"
              className="w-52 ml-10"
            />
          )}
        </div>

        <div className="flex gap-4 flex-wrap">
          <Button
            icon={localPathDetails?.activated ? "pi pi-times" : "pi pi-check"}
            label={localPathDetails?.activated ? "Deactivate" : "Activate"}
            severity="secondary"
            onClick={() => void handleToggleActivated()}
            loading={actionLoading === "activate"}
            disabled={!isLatestVersion}
          />
          <Button
            icon="pi pi-clone"
            label="Clone"
            severity="secondary"
            onClick={() => setIsCloneModalVisible(true)}
            disabled={!isLatestVersion}
          />
          <Button
            icon={localPathDetails?.restricted ? "pi pi-unlock" : "pi pi-lock"}
            label={localPathDetails?.restricted ? "Unrestrict" : "Restrict"}
            severity="secondary"
            onClick={() => void handleToggleRestrict()}
            loading={actionLoading === "restrict"}
            disabled={!isLatestVersion}
          />
          <Button
            icon="pi pi-download"
            label="SCORM"
            severity="secondary"
            onClick={() => setIsScormExportVisible(true)}
            disabled={!isLatestVersion}
          />
          {isCustomOrQuest && (
            <Button
              icon="pi pi-pencil"
              label="Edit"
              severity="secondary"
              onClick={() =>
                navigate(
                  AdminAppRoutes.routes.paths.routes.edit.route.replace(
                    ":id",
                    String(localPathDetails?.id ?? "")
                  )
                )
              }
              disabled={!isLatestVersion}
            />
          )}
          {isCustomOrQuest && (
            <Button
              icon="pi pi-trash"
              label="Delete"
              severity="danger"
              outlined
              onClick={handleDelete}
              loading={actionLoading === "delete"}
              disabled={!isLatestVersion}
            />
          )}
        </div>
      </div>

      {/* Title + description + badge */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <h1 className="atlas-h2-alt leading-tight">{localPathDetails?.name}</h1>
          {badgeSvg && (
            <div className="w-14 h-14 flex-shrink-0">
              <img
                src={badgeSvg}
                alt={`${(localPathDetails as { type?: string } | null)?.type ?? ""} icon`}
                className="w-full h-full object-contain opacity-20"
                onError={e => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          )}
        </div>
        <p className="body-lg max-w-xl">{localPathDetails?.description}</p>
      </div>

      {/* Stats grid */}
      <div
        className={`grid lg:grid-cols-2 ${pathType === "Quest" ? "xl:grid-cols-3" : "xl:grid-cols-4"} gap-8 mb-6`}
      >
        <div>
          <h4 className="atlas-h5 text-color-secondary mb-2">Total Points</h4>
          <p className="atlas-h2">
            {(localPathDetails as { totalPoints?: number } | null)?.totalPoints ?? 0}
          </p>
        </div>
        <div>
          <h4 className="atlas-h5 text-color-secondary mb-2">Lessons</h4>
          <p className="atlas-h2">
            {(localPathDetails as { lessonsCount?: number } | null)?.lessonsCount ?? 0}
          </p>
        </div>
        <div>
          <h4 className="atlas-h5 text-color-secondary mb-2">Duration</h4>
          <p className="atlas-h2">
            {formatMinutes(
              (localPathDetails as { totalDuration?: number } | null)?.totalDuration ?? 0
            )}
          </p>
        </div>
        {pathType !== "Quest" && (
          <div>
            <h4 className="atlas-h5 text-color-secondary mb-2">Difficulty</h4>
            <p className="atlas-h2">
              {(localPathDetails as { difficulty?: string } | null)?.difficulty ?? "—"}
            </p>
          </div>
        )}
      </div>

      <ConfirmDialog
        visible={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        message={`Are you sure you want to delete "${localPathDetails?.name}"? This action cannot be undone.`}
        header="Delete Path"
        icon="pi pi-exclamation-triangle"
        acceptLabel="Delete"
        rejectLabel="Cancel"
        acceptClassName="p-button-danger"
        accept={confirmDelete}
        reject={() => setShowDeleteConfirm(false)}
        maskStyle={{ backdropFilter: "blur(5px)" }}
      />

      <ScormExportDialog
        visible={isScormExportVisible}
        lessons={lessons as unknown as ScormLesson[]}
        onHide={() => setIsScormExportVisible(false)}
      />

      <ClonePathModal
        visible={isCloneModalVisible}
        onHide={() => setIsCloneModalVisible(false)}
        originalPath={localPathDetails as PathData}
        existingPathNames={allPathNames}
      />
    </div>
  );
};

export default PathDetailsAdmin;
